import dayjs from 'dayjs';
import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Form,
  Input,
  Radio,
  Select,
  Drawer,
  Button,
  DatePicker,
  useSnackbar,
  InputNumber,
} from 'design-system';

import { useDepartmentsByPrivilege } from 'src/hooks/use-departments-by-privilege';

import type { DepartmentPrivilegeBase } from 'src/utils/filterDepartmentsByPrivilege';

import { getAllUsers } from 'src/services/user';
import { PrivilegeType } from 'src/services/role';
import { queryKeys } from 'src/constants/queryKeys';
import type { ActionPlan } from 'src/services/actionPlan';
import { updateActionPlan, createActionPlan, ActionPlanStatus } from 'src/services/actionPlan';

import UploadMedia from 'src/components/UploadMedia';
import DepartmentTreeSelect from 'src/components/department-tree-select';
import { showUnsavedDataConfirm } from 'src/components/common/ConfirmDialog';

import styles from './editor.module.css';

interface Props {
  open: boolean;
  assessmentDimensionDisabled?: boolean;
  onClose: () => void;
  onSave?: () => void;
  action: 'create' | 'edit';
  data: ActionPlan | null;
  onSubmit?: (actionPlan: ActionPlan) => void;
  departmentPrivilegeBase?: DepartmentPrivilegeBase;
}

const acceptedFileTypes = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
};

const sourceOptions = [{ label: '手动创建', value: 'ManualCreated' }];

// 考核判断维度选项
export const assessmentDimensionOptions = [
  { label: '按时间', value: 'Duration' },
  { label: '按数量', value: 'Quantity' },
  { label: '是与否', value: 'Boolean' },
];

// 进度登记选项
const progressStatusOptions = [
  { label: '完成', value: 'Completed' },
  { label: '未完成', value: 'NotStarted' },
];

// 单位选项
const unitOptions = [
  { label: '元', value: '元' },
  // { label: '%', value: '%' },
  { label: '个/次', value: '个/次' },
];

export default function ActionPlanEditor({
  open,
  onClose,
  onSave,
  action,
  data,
  assessmentDimensionDisabled: initAssessmentDimensionDisabled = false,
  onSubmit,
  departmentPrivilegeBase = PrivilegeType.ACTION_PLAN,
}: Props) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [fileList, setFileList] = useState<string[]>([]);
  const [formChanged, setFormChanged] = useState(false);
  // 按传入权限获取可选部门；保留全量部门用于保存时对象查找
  const { departmentOptions, allDepartments: departments } =
    useDepartmentsByPrivilege(departmentPrivilegeBase);
  const assessmentDimensionDisabled = useMemo(
    () => data?.source === 'Project' || initAssessmentDimensionDisabled,
    [data, initAssessmentDimensionDisabled]
  );
  // 获取用户列表
  const { data: users = [] } = useQuery({
    queryKey: [queryKeys.getAllUsers],
    queryFn: getAllUsers,
  });

  // 监听表单字段变化
  const assessmentDimension = Form.useWatch('assessmentDimension', form);

  const unit = Form.useWatch('unit', form);

  useEffect(() => {
    if (open) {
      if (action === 'create') {
        form.resetFields();
        form.setFieldsValue({
          code: '',
          source: 'ManualCreated',
          unit: '元',
        });
        setFileList([]);
        setFormChanged(false);
      } else if (data && action === 'edit') {
        // 检查是否是数量维度且有有效的metadata数据
        const isQuantityDimension = data.assessmentDimension === 'Quantity';
        const hasValidMetadata =
          data.metadata &&
          (data.metadata.planQuantity !== null ||
            data.metadata.actualQuantity !== null ||
            data.metadata.unit !== null);

        // 准备所有表单值，包括数量字段
        const allFormValues: Record<string, unknown> = {
          departmentId: data.department?.id,
          coordinatorIds: data.coordinators?.map((user) => user.id) || [],
          code: data.code || '',
          source: data.source || 'ManualCreated',
          description: data.description,
          recordsDescription: data.records?.description || '',
          dateRange:
            data.planStartDate && data.planEndDate
              ? [dayjs(data.planStartDate), dayjs(data.planEndDate)]
              : null,
          // 关键：确保assessmentDimension在一开始就设置
          assessmentDimension: data.assessmentDimension,
          // 根据status反推progressStatus，优先使用metadata中的值，如果没有则根据status推断
          progressStatus: data.metadata?.progressStatus || data.status,
        };

        // 如果是数量维度且有有效的metadata，添加数量字段到初始值中
        if (isQuantityDimension && hasValidMetadata) {
          const planQuantity = data.metadata?.planQuantity;
          const actualQuantity = data.metadata?.actualQuantity;
          const unit = data.metadata?.unit || '元';

          // 将数量字段添加到表单值中，确保数字类型
          allFormValues.planQuantity =
            planQuantity !== null && planQuantity !== undefined ? Number(planQuantity) : undefined;
          allFormValues.actualQuantity =
            actualQuantity !== null && actualQuantity !== undefined
              ? Number(actualQuantity)
              : undefined;
          allFormValues.unit = unit;
        }

        // 一次性设置所有表单值
        form.setFieldsValue(allFormValues);

        setFileList(data?.records?.attachments ?? []);
        setFormChanged(false);
      }
      setIsEditing(action === 'edit');
    }
  }, [open, action, data, form]);

  // 监听考核判断维度变化，清理相关字段
  useEffect(() => {
    if (assessmentDimension && action === 'create') {
      // 只在创建模式下清理字段，编辑模式下保持原有数据
      if (assessmentDimension === 'Duration' || assessmentDimension === 'Boolean') {
        // 清理数量相关字段
        form.setFieldsValue({
          planQuantity: undefined,
          actualQuantity: undefined,
          unit: undefined,
        });
      } else if (assessmentDimension === 'Quantity') {
        // 清理进度状态字段
        form.setFieldsValue({
          progressStatus: undefined,
        });
      }
    }
  }, [assessmentDimension, form, action]);

  // 监听表单变化
  const handleFormValuesChange = () => {
    setFormChanged(true);
  };

  // 监听文件变化
  useEffect(() => {
    // 监听文件列表变化
    if (open && fileList.length > 0) {
      setFormChanged(true);
    }
  }, [fileList, open]);

  const updateActionPlanMutation = useMutation({
    mutationFn: (values: Partial<Omit<ActionPlan, 'id'>>) => {
      if (!data?.id) throw new Error('Missing id');
      const payload: Partial<ActionPlan> = {
        ...values,
        id: data.id,
      };
      return updateActionPlan(payload);
    },
    onSuccess: (updatedData: ActionPlan) => {
      // 使所有使用行动计划数据的查询失效
      queryClient.invalidateQueries([queryKeys.getAllActionPlans]);

      // 如果存在关联的差异分析ID，使其查询失效
      if (data?.varianceAnalysis?.id) {
        queryClient.invalidateQueries(['getVarianceAnalysis', data.varianceAnalysis.id]);
      }

      // 调用onSave回调通知父组件刷新数据
      if (onSave) {
        onSave();
      }
    },
  });

  const createActionPlanMutation = useMutation({
    mutationFn: (values: Partial<Omit<ActionPlan, 'id'>>) => createActionPlan(values),
    onSuccess: (createdData: ActionPlan) => {
      // 使所有使用行动计划数据的查询失效
      queryClient.invalidateQueries([queryKeys.getAllActionPlans]);

      // 调用onSave回调通知父组件刷新数据
      if (onSave) {
        onSave();
      }
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const [startDate, endDate] = values.dateRange || [null, null];

      // 将选择的coordinatorIds转换为coordinators对象数组
      const coordinators =
        values.coordinatorIds
          ?.map((id: number) => {
            const user = users.find((u) => u.id === id);
            return user
              ? { id: user.id, firstName: user.firstName, lastName: user.lastName }
              : null;
          })
          .filter(Boolean) || [];
      // 处理文件记录 - 根据新的 records 类型结构
      // 收集所有文件的 URL（包括已有的和新上传的）
      const allAttachments = fileList.filter(Boolean);

      // 构建metadata对象
      let metadata = {};
      let status: ActionPlanStatus | undefined; // 只在编辑时设置status

      if (values.assessmentDimension === 'Duration' || values.assessmentDimension === 'Boolean') {
        metadata = {
          progressStatus: values.progressStatus,
        };

        // 只在编辑时根据进度状态自动设置状态
        if (action === 'edit') {
          status = values?.progressStatus;
        }
      } else if (values.assessmentDimension === 'Quantity') {
        metadata = {
          planQuantity: values.planQuantity,
          actualQuantity: values.actualQuantity,
          unit: values.unit,
        };
        // 对于数量维度，编辑时保持原状态不变
        if (action === 'edit') {
          status = data?.status || ActionPlanStatus.NotStarted;
        }
      }

      // 构建基础payload，明确指定需要的字段，排除status和其他不需要的字段
      const basePayload = {
        code: values.code,
        source: values.source,
        description: values.description,
        assessmentDimension: values.assessmentDimension,
        planStartDate: startDate?.format('YYYY-MM-DD'),
        planEndDate: endDate?.format('YYYY-MM-DD'),
        coordinators,
        department: departments.find((dept) => dept.id === values.departmentId),
        records: {
          attachments: allAttachments,
          description: values.recordsDescription || '',
        },
        metadata,
      };

      // 只在编辑时添加status字段
      const payload =
        action === 'edit' && status !== undefined ? { ...basePayload, status } : basePayload;
      let res: ActionPlan | undefined;
      if (action === 'edit' && data) {
        res = await updateActionPlanMutation.mutateAsync(payload);

        // 成功后关闭抽屉并显示提示
        setFormChanged(false);
        enqueueSnackbar('行动计划保存成功', { variant: 'success' });
        onClose();
      } else if (action === 'create') {
        res = await createActionPlanMutation.mutateAsync(payload);

        // 成功后关闭抽屉并显示提示
        setFormChanged(false);
        enqueueSnackbar('行动计划创建成功', { variant: 'success' });
        onClose();
      }
      onSubmit && res && onSubmit(res);
    } catch (error) {
      // 错误处理

      // 检查是否是表单验证错误
      if (error && typeof error === 'object' && 'errorFields' in error) {
        const validationError = error as {
          errorFields: Array<{ name: string[]; errors: string[] }>;
        };
        const errorMessages = validationError.errorFields
          .map((field) => ` ${field.errors.join(', ')}`)
          .join('; ');
        enqueueSnackbar(`表单验证失败: ${errorMessages}`, { variant: 'error' });
        return;
      }

      // 显示更详细的错误信息
      if (error instanceof Error) {
        enqueueSnackbar(`保存失败: ${error.message}`, { variant: 'error' });
      } else {
        enqueueSnackbar('保存行动计划失败，请重试', { variant: 'error' });
      }
    }
  };

  const getTitle = () => {
    switch (action) {
      case 'create':
        return '新建行动计划';
      case 'edit':
        return '编辑行动计划';
      default:
        return '';
    }
  };

  // 处理关闭
  const handleClose = () => {
    if (formChanged) {
      showUnsavedDataConfirm(() => {
        // 在关闭时触发数据刷新
        if (action === 'edit' && data?.varianceAnalysis?.id) {
          queryClient.invalidateQueries(['getVarianceAnalysis', data.varianceAnalysis.id]);
        }
        onClose();
      });
    } else {
      // 即使没有修改，也需要在关闭时触发差异分析数据刷新
      if (action === 'edit' && data?.varianceAnalysis?.id) {
        queryClient.invalidateQueries(['getVarianceAnalysis', data.varianceAnalysis.id]);
      }
      onClose();
    }
  };

  return (
    <Drawer
      title={getTitle()}
      width={600}
      open={open}
      onClose={handleClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleClose} style={{ marginRight: 8 }}>
            取消
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            保存
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ flex: '110px' }}
        wrapperCol={{ flex: 'auto' }}
        initialValues={{
          source: 'ManualCreated',
          code: '',
          unit: '元',
        }}
        onValuesChange={handleFormValuesChange}
        colon={false}
      >
        <Form.Item name="code" label="编码" hidden>
          <Input placeholder="请输入编码" />
        </Form.Item>

        <Form.Item
          name="source"
          label="来源"
          hidden
          rules={[{ required: true, message: '请选择来源' }]}
        >
          <Select placeholder="请选择来源" options={sourceOptions} />
        </Form.Item>

        <Form.Item
          name="departmentId"
          label="考核部门"
          rules={[{ required: true, message: '请选择考核部门' }]}
        >
          <DepartmentTreeSelect placeholder="请选择考核部门" options={departmentOptions} />
        </Form.Item>

        <Form.Item
          name="coordinatorIds"
          label="责任人"
          rules={[{ required: true, message: '请选择责任人' }]}
        >
          <Select
            mode="multiple"
            placeholder="请选择责任人"
            showSearch
            filterOption={(input, option) => {
              if (!option?.label) return false;
              const label = String(option.label).toLowerCase();
              const searchValue = input.toLowerCase();
              return label.includes(searchValue);
            }}
            options={users.map((user) => ({
              label: `${user.lastName}${user.firstName}`,
              value: user.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="行动计划描述"
          rules={[{ required: true, message: '请输入行动计划描述' }]}
        >
          <Input.TextArea rows={4} placeholder="请输入行动计划描述" />
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="计划任务周期"
          rules={[{ required: true, message: '请选择计划任务周期' }]}
        >
          <DatePicker.RangePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="assessmentDimension"
          label="考核判断维度"
          rules={[{ required: true, message: '请选择考核判断维度' }]}
        >
          <Radio.Group
            disabled={assessmentDimensionDisabled}
            options={assessmentDimensionOptions}
          />
        </Form.Item>

        {/* 按时间和是与否维度的进度登记 - 仅在编辑时显示 */}
        {action === 'edit' &&
          (assessmentDimension === 'Duration' || assessmentDimension === 'Boolean') && (
            <Form.Item
              name="progressStatus"
              label="进度登记"
              rules={[{ required: true, message: '请选择进度状态' }]}
            >
              <Select placeholder="请选择进度状态" options={progressStatusOptions} />
            </Form.Item>
          )}

        {/* 数量维度的字段 - 仅在编辑时显示 */}
        {action === 'edit' &&
          (assessmentDimension === 'Quantity' ||
            (data?.assessmentDimension === 'Quantity' && !assessmentDimension)) && (
            <>
              <Form.Item
                name="planQuantity"
                label="目标数量"
                className={styles.editInputNumber}
                rules={[{ required: true, message: '请输入目标数量' }]}
              >
                <InputNumber
                  placeholder="请输入目标数量"
                  style={{ width: '100%' }}
                  min={0}
                  disabled={assessmentDimensionDisabled}
                  controls={false}
                  precision={2}
                  isCurrency
                  addonAfter={
                    <Form.Item
                      name="unit"
                      noStyle
                      rules={[{ required: true, message: '请选择单位' }]}
                    >
                      <Select
                        disabled={assessmentDimensionDisabled}
                        placeholder="单位"
                        style={{ width: 120, textAlign: 'left' }}
                        options={unitOptions}
                      />
                    </Form.Item>
                  }
                />
              </Form.Item>

              <Form.Item
                name="actualQuantity"
                label="实际完成数量"
                className={styles.editInputNumber}
                rules={[{ required: true, message: '请输入实际完成数量' }]}
              >
                <InputNumber
                  placeholder="请输入实际完成数量"
                  style={{ width: '100%' }}
                  min={0}
                  isCurrency
                  controls={false}
                  precision={2}
                  addonAfter={
                    <Select
                      options={[]}
                      disabled
                      value={form.getFieldValue('unit') || unit || '单位'}
                      style={{ width: 120, textAlign: 'left' }}
                    />
                  }
                />
              </Form.Item>
            </>
          )}

        {action === 'edit' && (
          <>
            <Form.Item label="过程记录">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Form.Item name="recordsDescription" style={{ marginBottom: 0 }}>
                  <Input.TextArea rows={3} placeholder="请输入过程记录" />
                </Form.Item>

                <Form.Item name="records" style={{ marginBottom: 0 }}>
                  <UploadMedia<string[]> value={fileList} onChange={setFileList} maxCount={5} />
                </Form.Item>
              </div>
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
}
